import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { optimisticMutation, urlPredicate } from "@/utils/optimistic";
import { useUpdateWorkspace } from "@/api/account";
import {
  SidebarItem,
  UpdateSidebarRequest,
  getSidebarItem,
  getPinnedItems,
} from "@/models/sidebar";
import { Workspace } from "@/models/account";
import { Dashboard } from "@/models/dashboard";
import {
  useUpdateDashboard,
  useCreateDashboard,
  useDeleteDashboard,
} from "@/api/dashboard";
import { useWorkspace } from "@/hooks/use-workspace";
import logger from "@/utils/logger";
import {
  useSidebarStore,
  DEFAULT_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
} from "./store";

// ============================================================================
// Types
// ============================================================================

interface ItemPreviewState {
  previewItemId: string | null;
  isPreviewActive: boolean;
}

interface ContextMenuState {
  position: { x: number; y: number };
  dashboard: Dashboard;
}

// Preview delay (ms)
const HOVER_DELAY = 300;

interface SidebarContextValue {
  // Workspace with sidebar data
  workspace: Workspace | null;
  isLoading: boolean;
  isOpen: boolean;
  sidebarWidth: number;
  isLocked: boolean;

  // Toggle sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarWidth: (width: number) => void;
  toggleLock: () => void;

  // Item management (dashboards and groups)
  expandedItems: string[];
  toggleItem: (itemId: string) => void;
  pinItem: (
    itemId: string,
    itemType: "dashboard" | "group",
    pinned?: boolean
  ) => void;
  reorderItems: (items: Array<{ id: string; type: "dashboard" | "group" }>) => void;
  getItemConfig: (itemId: string) => SidebarItem | null;

  // Active item
  activeItemId: string | null;
  setActiveItem: (itemId: string | null) => void;

  // Dashboard operations (delete, duplicate, rename, change color)
  deleteDashboard: (dashboardId: string) => Promise<void>;
  duplicateDashboard: (dashboard: Dashboard) => Promise<Dashboard | null>;
  renameDashboard: (dashboardId: string, newName: string) => Promise<void>;
  changeDashboardColor: (dashboardId: string, color: string) => Promise<void>;

  // Inline rename
  renamingDashboardId: string | null;
  startRenamingDashboard: (dashboardId: string) => void;
  cancelRenamingDashboard: () => void;

  // Context menu
  contextMenu: ContextMenuState | null;
  openContextMenu: (e: React.MouseEvent, dashboard: Dashboard) => void;
  closeContextMenu: () => void;

  // Item preview
  previewState: ItemPreviewState;
  startPreview: (itemId: string) => void;
  cancelPreview: () => void;
  confirmPreview: () => void;

  // Workspace swipe
  currentWorkspaceIndex: number;
  setCurrentWorkspaceIndex: (index: number) => void;
  swipeDirection: "left" | "right" | null;
  setSwipeDirection: (direction: "left" | "right" | null) => void;

  // Helpers
  getPinnedDashboards: (dashboards: Dashboard[]) => Dashboard[];
  getPinnedItems: () => SidebarItem[];
  
  // Unsaved changes tracking (stub - to be implemented with proper state management)
  hasUnsavedChanges: (dashboardId: string) => boolean;
  saveDashboard: (dashboardId: string) => Promise<void>;
  isSavingDashboard: (dashboardId: string) => boolean;
}

// ============================================================================
// Context
// ============================================================================

const SidebarContext = createContext<SidebarContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const { currentWorkspace, workspaces, isLoading } = useWorkspace();

  // Zustand store for UI state (persisted in localStorage)
  const {
    isOpen,
    setIsOpen,
    isLocked,
    toggleLock: storeToggleLock,
    sidebarWidth: storedSidebarWidth,
    setSidebarWidth: setStoredSidebarWidth,
    currentWorkspaceIndex,
    setCurrentWorkspaceIndex,
  } = useSidebarStore();

  // API hooks
  const updateWorkspaceMutation = useUpdateWorkspace();
  const updateDashboardMutation = useUpdateDashboard();
  const createDashboardMutation = useCreateDashboard();
  const deleteDashboardMutation = useDeleteDashboard();

  // Track previous workspaces length to detect additions/removals
  const prevWorkspacesLengthRef = useRef<number>(workspaces.length);
  const initialSyncDoneRef = useRef<boolean>(false);

  // Synchroniser currentWorkspaceIndex avec le workspace réellement sélectionné
  useEffect(() => {
    if (!isLoading && currentWorkspace && workspaces.length > 0) {
      const workspacesChanged =
        prevWorkspacesLengthRef.current !== workspaces.length;
      const isInitialSync = !initialSyncDoneRef.current;

      if (isInitialSync || workspacesChanged) {
        const correctIndex = workspaces.findIndex(
          (w) => w.id === currentWorkspace.id
        );
        if (correctIndex !== -1 && correctIndex !== currentWorkspaceIndex) {
          setCurrentWorkspaceIndex(correctIndex);
        }
        initialSyncDoneRef.current = true;
        prevWorkspacesLengthRef.current = workspaces.length;
      }
    }
  }, [
    isLoading,
    currentWorkspace,
    workspaces,
    currentWorkspaceIndex,
    setCurrentWorkspaceIndex,
  ]);

  // Local state for preview, swipe, context menu, and inline rename
  const [previewState, setPreviewState] = useState<ItemPreviewState>({
    previewItemId: null,
    isPreviewActive: false,
  });
  const [swipeDirection, setSwipeDirection] = useState<
    "left" | "right" | null
  >(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(
    null
  );
  const [renamingDashboardId, setRenamingDashboardId] = useState<string | null>(
    null
  );
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  // Refs (not state to avoid re-renders)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Invalidate workspaces query on mutations
  const invalidateWorkspaces = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0];
        if (typeof queryKey === "string") {
          try {
            const config = JSON.parse(queryKey);
            return config.url?.includes("workspace");
          } catch {
            return false;
          }
        }
        return false;
      },
    });
  }, [queryClient]);

  // Invalidate dashboards query
  const invalidateDashboards = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0];
        if (typeof queryKey === "string") {
          try {
            const config = JSON.parse(queryKey);
            return config.url?.includes("dashboard");
          } catch {
            return false;
          }
        }
        return false;
      },
    });
  }, [queryClient]);

  const workspacePredicate = urlPredicate("/workspaces/");

  // Helper to update workspace sidebar fields with optimistic cache update
  const updateSidebar = useCallback(
    async (updates: UpdateSidebarRequest) => {
      if (!currentWorkspace?.id) {
        logger.error("No workspace selected");
        return;
      }

      try {
        await optimisticMutation<Workspace[]>({
          queryClient,
          queryPredicate: workspacePredicate,
          updater: (workspaces) => {
            if (!Array.isArray(workspaces)) return workspaces;
            return workspaces.map((ws) =>
              ws.id === currentWorkspace.id ? { ...ws, ...updates } : ws,
            );
          },
          mutationFn: () =>
            updateWorkspaceMutation.mutateAsync({
              uriParams: { id: currentWorkspace.id },
              ...updates,
            }),
          onSettled: () => invalidateWorkspaces(),
        });
      } catch (error) {
        logger.error("Failed to update workspace sidebar:", error);
      }
    },
    [currentWorkspace, queryClient, updateWorkspaceMutation, invalidateWorkspaces],
  );

  // ============================================================================
  // Sidebar width (from Zustand store, persisted locally)
  // ============================================================================

  const sidebarWidth = storedSidebarWidth;

  const setSidebarWidth = useCallback(
    (width: number) => {
      setStoredSidebarWidth(width);
    },
    [setStoredSidebarWidth]
  );

  // ============================================================================
  // Sidebar lock (stay expanded) - from Zustand store
  // ============================================================================

  const toggleLock = useCallback(() => {
    storeToggleLock();
  }, [storeToggleLock]);

  // ============================================================================
  // Sidebar toggle
  // ============================================================================

  const toggleSidebar = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const setSidebarOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
    },
    [setIsOpen]
  );

  // ============================================================================
  // Item management (dashboards and groups)
  // ============================================================================

  const expandedItems = useMemo(
    () => currentWorkspace?.sidebar_expanded_items || [],
    [currentWorkspace]
  );

  const toggleItem = useCallback(
    async (itemId: string) => {
      const currentExpanded = currentWorkspace?.sidebar_expanded_items || [];
      const isCurrentlyExpanded = currentExpanded.includes(itemId);

      const newExpanded = isCurrentlyExpanded
        ? currentExpanded.filter((id) => id !== itemId)
        : [...currentExpanded, itemId];

      await updateSidebar({ sidebar_expanded_items: newExpanded });
    },
    [currentWorkspace, updateSidebar]
  );

  const pinItem = useCallback(
    async (
      itemId: string,
      itemType: "dashboard" | "group",
      pinned: boolean = true
    ) => {
      const currentItems = currentWorkspace?.sidebar_items || [];
      const existingItem = currentItems.find((i) => i.item_id === itemId);

      let newItems: SidebarItem[];
      if (existingItem) {
        newItems = currentItems.map((item) =>
          item.item_id === itemId ? { ...item, pinned } : item
        );
      } else {
        const maxOrder = Math.max(0, ...currentItems.map((i) => i.order));
        newItems = [
          ...currentItems,
          { item_id: itemId, item_type: itemType, order: maxOrder + 1, pinned },
        ];
      }

      await updateSidebar({ sidebar_items: newItems });
    },
    [currentWorkspace, updateSidebar]
  );

  const reorderItems = useCallback(
    async (items: Array<{ id: string; type: "dashboard" | "group" }>) => {
      const currentItems = currentWorkspace?.sidebar_items || [];

      // Preserve sidebar items not included in the reorder (e.g. pinned-only items)
      const reorderedIds = new Set(items.map((i) => i.id));
      const preservedItems = currentItems.filter(
        (i) => !reorderedIds.has(i.item_id)
      );

      const newItems = items.map(({ id, type }, index) => {
        const existing = currentItems.find((i) => i.item_id === id);
        if (existing) {
          return { ...existing, order: index };
        }
        return {
          item_id: id,
          item_type: type,
          order: index,
          pinned: false,
        };
      });

      await updateSidebar({ sidebar_items: [...newItems, ...preservedItems] });
    },
    [currentWorkspace, updateSidebar]
  );

  const getItemConfig = useCallback(
    (itemId: string): SidebarItem | null => {
      return getSidebarItem(currentWorkspace, itemId);
    },
    [currentWorkspace]
  );

  const getPinnedDashboardsFn = useCallback(
    (dashboards: Dashboard[]): Dashboard[] => {
      const pinnedItemIds = new Set(
        getPinnedItems(currentWorkspace)
          .filter((item) => item.item_type === "dashboard")
          .map((item) => item.item_id)
      );

      return dashboards
        .filter((d) => d.id && pinnedItemIds.has(d.id))
        .sort((a, b) => {
          const configA = getItemConfig(a.id!);
          const configB = getItemConfig(b.id!);
          return (configA?.order || 0) - (configB?.order || 0);
        });
    },
    [currentWorkspace, getItemConfig]
  );

  const getPinnedItemsFn = useCallback((): SidebarItem[] => {
    return getPinnedItems(currentWorkspace);
  }, [currentWorkspace]);

  // Stub function for hasUnsavedChanges - always returns false for now
  // TODO: Implement proper unsaved changes tracking with dashboard state management
  const hasUnsavedChangesFn = useCallback((_dashboardId: string): boolean => {
    return false;
  }, []);

  // Stub function for saveDashboard - no-op for now
  // TODO: Implement proper save functionality
  const saveDashboardFn = useCallback(async (_dashboardId: string): Promise<void> => {
    // No-op stub
  }, []);

  // Stub function for isSavingDashboard - always returns false for now
  // TODO: Implement proper saving state tracking
  const isSavingDashboardFn = useCallback((_dashboardId: string): boolean => {
    return false;
  }, []);

  // ============================================================================
  // Active item management (local state only)
  // ============================================================================

  const setActiveItem = useCallback((itemId: string | null) => {
    setActiveItemId(itemId);
  }, []);

  // ============================================================================
  // Dashboard operations (delete, duplicate, rename, change color)
  // ============================================================================

  const deleteDashboardFn = useCallback(
    async (dashboardId: string) => {
      try {
        await deleteDashboardMutation.mutateAsync({
          uriParams: { id: dashboardId },
        });
        invalidateDashboards();
        invalidateWorkspaces();
      } catch (error) {
        logger.error("Failed to delete dashboard:", error);
        throw error;
      }
    },
    [deleteDashboardMutation, invalidateDashboards, invalidateWorkspaces]
  );

  const duplicateDashboard = useCallback(
    async (dashboard: Dashboard): Promise<Dashboard | null> => {
      if (!dashboard.id) return null;

      const newName = `${dashboard.name || "Dashboard"} (copy)`;

      try {
        // Note: Widgets are now separate documents referencing dashboards.
        // Duplicating a dashboard creates a new empty dashboard.
        // To duplicate widgets, they would need to be fetched and recreated separately.
        const newDashboard = await createDashboardMutation.mutateAsync({
          name: newName,
          description: dashboard.description || undefined,
          filters: dashboard.filter || undefined,
        });
        invalidateDashboards();
        invalidateWorkspaces();
        return newDashboard as unknown as Dashboard;
      } catch (error) {
        logger.error("Failed to duplicate dashboard:", error);
        throw error;
      }
    },
    [createDashboardMutation, invalidateDashboards, invalidateWorkspaces]
  );

  const dashboardPredicate = urlPredicate("dashboard");

  const renameDashboard = useCallback(
    async (dashboardId: string, newName: string) => {
      try {
        await optimisticMutation<Dashboard[]>({
          queryClient,
          queryPredicate: dashboardPredicate,
          updater: (dashboards) =>
            dashboards.map((d) =>
              d.id === dashboardId ? { ...d, name: newName } : d,
            ),
          mutationFn: () =>
            updateDashboardMutation.mutateAsync({
              uriParams: { id: dashboardId },
              name: newName,
            }),
          onSettled: () => {
            invalidateDashboards();
            invalidateWorkspaces();
          },
        });
      } catch (error) {
        logger.error("Failed to rename dashboard:", error);
        throw error;
      }
    },
    [queryClient, updateDashboardMutation, invalidateDashboards, invalidateWorkspaces],
  );

  const changeDashboardColor = useCallback(
    async (dashboardId: string, color: string) => {
      try {
        await optimisticMutation<Dashboard[]>({
          queryClient,
          queryPredicate: dashboardPredicate,
          updater: (dashboards) =>
            dashboards.map((d) =>
              d.id === dashboardId ? { ...d, color } : d,
            ),
          mutationFn: () =>
            updateDashboardMutation.mutateAsync({
              uriParams: { id: dashboardId },
              color,
            }),
          onSettled: () => {
            invalidateDashboards();
            invalidateWorkspaces();
          },
        });
      } catch (error) {
        logger.error("Failed to change dashboard color:", error);
        throw error;
      }
    },
    [queryClient, updateDashboardMutation, invalidateDashboards, invalidateWorkspaces],
  );

  // ============================================================================
  // Context Menu
  // ============================================================================

  const openContextMenu = useCallback(
    (e: React.MouseEvent, dashboard: Dashboard) => {
      e.preventDefault();
      setContextMenu({
        position: { x: e.clientX, y: e.clientY },
        dashboard,
      });
    },
    []
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // ============================================================================
  // Inline rename
  // ============================================================================

  const startRenamingDashboard = useCallback((dashboardId: string) => {
    setRenamingDashboardId(dashboardId);
    setContextMenu(null);
  }, []);

  const cancelRenamingDashboard = useCallback(() => {
    setRenamingDashboardId(null);
  }, []);

  // ============================================================================
  // Item preview
  // ============================================================================

  const startPreview = useCallback((itemId: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setPreviewState({ previewItemId: itemId, isPreviewActive: true });
    }, HOVER_DELAY);
  }, []);

  const cancelPreview = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setPreviewState({ previewItemId: null, isPreviewActive: false });
  }, []);

  const confirmPreview = useCallback(() => {
    if (previewState.previewItemId) {
      setActiveItem(previewState.previewItemId);
    }
    cancelPreview();
  }, [previewState.previewItemId, setActiveItem, cancelPreview]);

  // Keyboard handler (ESC to cancel preview or close context menu)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (contextMenu) {
          closeContextMenu();
        } else if (previewState.isPreviewActive) {
          cancelPreview();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    previewState.isPreviewActive,
    cancelPreview,
    contextMenu,
    closeContextMenu,
  ]);

  // ============================================================================
  // Context value
  // ============================================================================

  const stableCallbacks = useMemo(
    () => ({
      toggleSidebar,
      setSidebarOpen,
      setSidebarWidth,
      toggleLock,
      toggleItem,
      pinItem,
      reorderItems,
      setActiveItem,
      deleteDashboard: deleteDashboardFn,
      duplicateDashboard,
      renameDashboard,
      changeDashboardColor,
      startRenamingDashboard,
      cancelRenamingDashboard,
      openContextMenu,
      closeContextMenu,
      startPreview,
      cancelPreview,
      confirmPreview,
      setCurrentWorkspaceIndex,
      setSwipeDirection,
    }),
    [
      toggleSidebar,
      setSidebarOpen,
      setSidebarWidth,
      toggleLock,
      toggleItem,
      pinItem,
      reorderItems,
      setActiveItem,
      deleteDashboardFn,
      duplicateDashboard,
      renameDashboard,
      changeDashboardColor,
      startRenamingDashboard,
      cancelRenamingDashboard,
      openContextMenu,
      closeContextMenu,
      startPreview,
      cancelPreview,
      confirmPreview,
      setCurrentWorkspaceIndex,
      setSwipeDirection,
    ]
  );

  const dataGetters = useMemo(
    () => ({
      getItemConfig,
      getPinnedDashboards: getPinnedDashboardsFn,
      getPinnedItems: getPinnedItemsFn,
      hasUnsavedChanges: hasUnsavedChangesFn,
      saveDashboard: saveDashboardFn,
      isSavingDashboard: isSavingDashboardFn,
    }),
    [getItemConfig, getPinnedDashboardsFn, getPinnedItemsFn, hasUnsavedChangesFn, saveDashboardFn, isSavingDashboardFn]
  );

  const value = useMemo<SidebarContextValue>(
    () => ({
      workspace: currentWorkspace || null,
      isLoading,
      isOpen,
      sidebarWidth,
      isLocked,
      expandedItems,
      activeItemId,
      previewState,
      contextMenu,
      renamingDashboardId,
      currentWorkspaceIndex,
      swipeDirection,
      ...stableCallbacks,
      ...dataGetters,
    }),
    [
      currentWorkspace,
      isLoading,
      isOpen,
      sidebarWidth,
      isLocked,
      expandedItems,
      activeItemId,
      previewState,
      contextMenu,
      renamingDashboardId,
      currentWorkspaceIndex,
      swipeDirection,
      stableCallbacks,
      dataGetters,
    ]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useSidebarContext = (): SidebarContextValue => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
};

// Re-export types and helpers
export type { SidebarItem };

// Export sidebar width constants
export {
  DEFAULT_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
};
